import { useState, useEffect, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import MainFeedHeader from '@/components/MainFeedHeader';

interface DrillInLayoutProps {
  title: string;
  groupId: string;
  children: ReactNode;
}

export const DrillInLayout = ({ title, groupId, children }: DrillInLayoutProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [groupName, setGroupName] = useState('');
  const [isCommissioner, setIsCommissioner] = useState(false);

  useEffect(() => {
    if (!groupId || !user) return;
    const load = async () => {
      const [{ data: g }, { data: membership }] = await Promise.all([
        supabase.from('groups').select('name').eq('id', groupId).single(),
        supabase.from('group_members').select('role').eq('group_id', groupId).eq('user_id', user.id).maybeSingle(),
      ]);
      setGroupName(g?.name || '');
      setIsCommissioner(membership?.role === 'commissioner');
    };
    load();
  }, [groupId, user]);

  return (
    <div className="min-h-screen bg-background font-leon">
      {/* Main header — always visible, sticky top */}
      <MainFeedHeader groupId={groupId} groupName={groupName || 'טוען...'} isCommissioner={isCommissioner} />

      {/* Drill-in back bar — below main header */}
      <div className="fixed top-14 left-0 right-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto flex items-center justify-between h-10 px-4">
          <button
            onClick={() => {
              const from = searchParams.get('from');
              if (from === 'leaderboard') {
                navigate(`/group/${groupId}/leaderboard`);
              } else {
                navigate(`/group/${groupId}`);
              }
            }}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowRight className="w-4 h-4" />
            <span className="text-sm">חזרה</span>
          </button>

          <span className="font-bold text-sm absolute left-1/2 -translate-x-1/2">{title}</span>

          <div className="w-[44px]" aria-hidden />
        </div>
      </div>

      {/* Content — offset for both bars (h-14 + h-10 = 24 = pt-24) */}
      <main className="pt-24 flex-1">{children}</main>
    </div>
  );
};

export default DrillInLayout;
